<template>
  <div ref="mapEl" class="cn-app-map" />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useGoogleMaps } from '@/composables';

interface Props {
  markers: any[];
  activeMarker?: number;
}
const ZOOM = 17;

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:activeMarker', data: number): void;
}>();

const config = useRuntimeConfig().public.frontkit;

const mapEl = ref<HTMLElement | null>(null);

let googleMaps: any = null;
let MarkerCtor: any = null;
let map: any = null;
let clusterer: any = null;
let markerObjects: any[] = [];

const markerIcon = (index: number) =>
  index === props.activeMarker ? '/svg/marker-active.svg' : '/svg/marker.svg';

const fitMarkersBounds = () => {
  if (!map || !googleMaps || !props.markers.length) return;
  const bounds = new googleMaps.LatLngBounds();
  props.markers.forEach((marker) => bounds.extend(marker.position));
  map.fitBounds(bounds);
  if (props.markers.length < 2) map.setZoom(ZOOM);
};

const renderMarkers = () => {
  if (!map || !googleMaps) return;

  if (!clusterer) clusterer = new MarkerClusterer({ map });
  clusterer.clearMarkers();
  markerObjects = props.markers.map((marker, index) => {
    const markerObject = new MarkerCtor({
      position: marker.position,
      title: marker.title,
      icon: markerIcon(index),
    });
    markerObject.addListener('click', () => emit('update:activeMarker', index));
    return markerObject;
  });
  clusterer.addMarkers(markerObjects);
};

onMounted(async () => {
  const loader = useGoogleMaps(config.gmapId);

  const { Map: GMap } = await loader.importLibrary('maps');

  // `window.google` is only typed once the Maps types are pulled in, and this
  // branch's lint config rejects a bare @ts-ignore, so cast instead of
  // suppressing — that holds whether or not the typings are present.
  googleMaps = (window as any).google.maps;
  MarkerCtor = googleMaps.Marker;

  map = new GMap(mapEl.value, {
    center: { lat: 0, lng: 0 },
    zoom: 7,
    streetViewControl: false,
  });

  renderMarkers();
  fitMarkersBounds();
});

watch(
  () => props.markers,
  () => {
    renderMarkers();
    fitMarkersBounds();
  },
);

watch(
  () => props.activeMarker,
  (value) => {
    if (value === null || value === undefined || !map) return;
    markerObjects.forEach((marker, index) => marker.setIcon(markerIcon(index)));
    map.panTo(props.markers[value].position);
    map.setZoom(ZOOM);
  },
);

onBeforeUnmount(() => {
  clusterer?.clearMarkers();
  clusterer?.setMap(null);
  markerObjects.forEach((marker) => marker.setMap(null));
});
</script>

<style lang="scss">
.cn-app-map {
  width: 100%;
  height: 100%;
}
</style>
